/**
 * THE PATIENT-FACING VOCABULARY OF A DOCUMENT RETURN — shared, and deliberately homeless.
 *
 * `tasks/practice-documents.md` §19–§22. These five exports were born inside
 * `core/services/public-patient-document.service.ts`, alongside the three endpoints of the
 * standalone `/patient-document/<token>` page. §19 retired that rail — documents ride the
 * `PatientInvite` rail now — and the service went with it, so anything that outlived the page had
 * to move somewhere that cannot itself be retired by the next wave.
 *
 * ── WHY A BARE MODULE AND NOT A SERVICE, AND NOT A COMPONENT FOLDER ──────────────────────────
 *
 * Nothing here injects anything or touches HTTP: they are pure functions over an error body and
 * two constants about files. Putting them on an `@Injectable` would mean a component had to ask
 * the injector for a string constant. Putting them under the invite wizard's folder would repeat
 * the mistake this file exists to correct — the wizard is a component, components get retired, and
 * the next thing that needs this copy would import it from a page that is on its way out.
 *
 * ── THE FIVE REFUSAL CODES ARE A CONTRACT, NOT A LOCAL CHOICE ────────────────────────────────
 *
 * `UPLOAD_REFUSAL_CODES` names exactly what the return endpoint can answer with. THE SERVER SPEAKS
 * THESE STRINGS: `POST /api/patient-invites/:id/documents/:requestId/files` returns them in
 * `error.error`, and `readUploadRefusal` switches on them. Renaming one here does not rename it
 * there — it silently drops that refusal into the `default` branch, where the patient is told to
 * check their connection about a file the practice will never accept. They change on both sides or
 * not at all.
 *
 * ── THE SERVER'S OWN MESSAGE IS PREFERRED, ALWAYS ────────────────────────────────────────────
 *
 * It is the only party that can name the actual numbers — "you attached 14 files, and the limit is
 * 10", "\"IMG_0042.HEIC\" is not a document we can accept". The fallbacks below exist only for a
 * refusal that arrives without a body, and a client paraphrase would drop exactly the part that
 * tells the patient what to do next.
 */

/**
 * The five-state marker a document endpoint puts on a refusal.
 *
 * `ok` is 200; `not_found` is 404; the other three are 410. The invite routes inherited these
 * words from the standalone rail and kept them, which is why the wizard can read a refusal with
 * the same helper the retired page used.
 */
export type PublicDocumentState = 'ok' | 'not_found' | 'completed' | 'expired' | 'revoked';

/** The shape of a refusal body from any of the document endpoints. */
export interface PublicDocumentErrorBody {
  success?: boolean;
  status?: string;
  error?: string;
  message?: string;
  /** The upload refusals carry their own ceilings and the offending file's name. */
  maxFiles?: number;
  maxTotalBytes?: number;
  totalBytes?: number;
  fileCount?: number;
  fileName?: string | null;
  accepts?: string;
  /** A return that had already landed. A SUCCESS wearing a refusal's clothes — see §22.1. */
  alreadyReturned?: boolean;
}

/**
 * Read the five-state marker off a failed call.
 *
 * Returns `null` for anything that is not one of the five — a 500, a rate-limit 429, a dropped
 * connection, or one of the upload refusals — because those are a DIFFERENT screen: they are
 * retryable and the other four are not.
 */
export function readPublicDocumentState(error: unknown): PublicDocumentState | null {
  const body = (error as { error?: PublicDocumentErrorBody } | null)?.error;
  const status = body?.status;
  if (status === 'not_found' || status === 'completed' || status === 'expired'
    || status === 'revoked' || status === 'ok') {
    return status;
  }
  return null;
}

/** The server's message, verbatim, when it sent one. It is written for the patient, not for us. */
export function readPublicDocumentMessage(error: unknown): string | null {
  const body = (error as { error?: PublicDocumentErrorBody } | null)?.error;
  const message = body?.message;
  return typeof message === 'string' && message !== '' ? message : null;
}

/**
 * The five refusal codes the document return endpoint can answer with.
 *
 * THE SERVER SPEAKS THESE. See the header — they are not a local naming choice.
 */
export const UPLOAD_REFUSAL_CODES = [
  'PATIENT_DOCUMENT_NO_FILES',
  'PATIENT_DOCUMENT_TOO_MANY_FILES',
  'PATIENT_DOCUMENT_TOO_LARGE',
  'PATIENT_DOCUMENT_FILE_TYPE_NOT_ACCEPTED',
  'TOKEN_MISMATCH',
];

/**
 * An upload refusal, as a sentence the patient can act on.
 *
 * THE SERVER'S OWN MESSAGE IS PREFERRED IN EVERY CASE — see the header.
 */
export function readUploadRefusal(error: unknown): { code: string; message: string } {
  const body = (error as { error?: PublicDocumentErrorBody } | null)?.error;
  const code = (body && body.error) || 'UNKNOWN';
  const served = readPublicDocumentMessage(error);
  if (served) return { code, message: served };

  switch (code) {
    case 'PATIENT_DOCUMENT_NO_FILES':
      return { code, message: 'Attach the completed document before sending it.' };
    case 'PATIENT_DOCUMENT_TOO_MANY_FILES':
      return { code, message: 'That is more files than the practice can accept. Please send fewer.' };
    case 'PATIENT_DOCUMENT_TOO_LARGE':
      return { code, message: 'Your files are too large to send. Please send smaller photographs.' };
    case 'PATIENT_DOCUMENT_FILE_TYPE_NOT_ACCEPTED':
      return { code, message: 'One of your files is not a document the practice can accept.' };
    case 'TOKEN_MISMATCH':
      // No per-document link exists any more (§19), so this no longer says "this document link".
      // The patient holds ONE link — their invite — and re-opening it is the whole of the fix.
      return {
        code,
        message: 'This does not match the document you are trying to send back. Please open the '
          + 'link the practice sent you again.',
      };
    default:
      return {
        code,
        message: 'We could not send your files. Please check your connection and try again.',
      };
  }
}

/**
 * What the file picker offers, and what this client will put in an upload.
 *
 * PDFs AND IMAGES (§5): the realistic return is a phone photograph of each page, and a page filter
 * that accepted only PDFs would send a patient with no scanner back to the practice.
 *
 * NOTHING HERE IS A SECURITY CONTROL — the server sniffs the first sixteen bytes of every file and
 * decides for itself. This list only saves a doomed upload over a mobile connection, and it is kept
 * NO LOOSER than the server's own signature list (PDF · JPEG · PNG · GIF · WEBP · TIFF · BMP ·
 * HEIC), because a client that accepts what the server refuses has already cost the upload.
 */
export const ACCEPTED_RETURN_TYPES = 'application/pdf,image/*';

/* ════════════════════════════════════════════════════════════════════════════════════════════
 * THE TIER — `tasks/practice-documents.md` §1.1 and §5
 * ══════════════════════════════════════════════════════════════════════════════════════════ */

/**
 * How a patient completes one document.
 *
 * `'embedded'` — tier A. The blank is a real AcroForm and the patient fills it in on screen.
 * `'download'` — tier C. Download, complete it away from the screen, send the pages back.
 *
 * Tier B (a flat PDF with a text layer, synthesised into an AcroForm) is not built and has no
 * value of its own: when it lands it becomes `'embedded'`, because the artefact it produces is
 * a fillable PDF and the patient-facing path is identical.
 */
export type DocumentFillMode = 'embedded' | 'download';

/** The only string that means "fill it in on screen". */
export const EMBEDDED_FILL_MODE: DocumentFillMode = 'embedded';
export const DOWNLOAD_FILL_MODE: DocumentFillMode = 'download';

/**
 * Read the tier off whatever the server sent, and read ANYTHING ELSE as tier C.
 *
 * `tasks/practice-documents.md` §3.2 decision 4 says of `hasAcroForm`'s zero default that it
 * "reads as tier C — the path that works for every document", and that is the principle here:
 * `undefined` (a server that predates Phase 5), `null`, `''`, a future tier this client has never
 * heard of and a typo all resolve to `'download'`. Tier C works for every document; guessing
 * `'embedded'` sends a patient to a viewer that may have nothing to fill in.
 *
 * WHITELIST, NOT `!== 'download'`. The negative form would turn an unknown future value into an
 * on-screen viewer, which is the failure this function exists to prevent.
 */
export function readFillMode(value: unknown): DocumentFillMode {
  return value === EMBEDDED_FILL_MODE ? EMBEDDED_FILL_MODE : DOWNLOAD_FILL_MODE;
}

export function isAcceptedReturnFile(file: File | null | undefined): boolean {
  if (!file) return false;
  const type = (file.type || '').toLowerCase();
  if (type === 'application/pdf' || type.indexOf('image/') === 0) return true;
  // A phone that hands over an empty `type` is ordinary, so the extension is the fallback claim.
  const name = (file.name || '').toLowerCase();
  return /\.(pdf|jpe?g|png|gif|webp|tiff?|bmp|heic|heif)$/.test(name);
}
